import React, { useEffect, useState } from 'react'
import { useApolloClient, useReactiveVar } from '@apollo/client'
import { useHistory } from 'react-router'
import { Spin } from 'src/antd'

import { GET_ACTIVE_PROJECTS_DETAILS, GET_ME } from 'src/api'
import { iamClient } from 'src/lib/api-client'
import type { ProfileResponse } from 'src/api/iam/profile.types'
import { GetActiveProjectsDetails } from 'src/api/__generated__/types'
import { ALL_USER_FILTER_COLLECTIONS } from 'src/hooks/useSavedFilters/api'
import { setRouting, setSavedFilters, setUser } from 'src/reactive-variables'
import { routingVar } from 'src/reactive-variables/routing'
import { impersonatorTokenActiveProjectsKey } from 'src/utils/impersonator'
import TokenStorage from 'src/utils/tokenStorage'

import { checkForRedirects } from './utils'

const Router: React.FC = ({ children }) => {
  const history = useHistory()
  const { init } = useReactiveVar(routingVar)
  const [anyOtherActionRequired, setAnyOtherActionRequired] = useState(true)
  const client = useApolloClient()
  useEffect(() => {
    // init when the app is loaded;
    async function getUser() {
      try {
        // Fetch user profile from REST API instead of GraphQL
        const profileResponse = await iamClient.get<ProfileResponse>(
          '/users/me/profile'
        )
        const profile = profileResponse.data
        // Also fetch GraphQL data for additional fields
        const response = await client.query({ query: GET_ME })
        const lastActiveProjectsRaw = localStorage.getItem(
          impersonatorTokenActiveProjectsKey
        )
        // Filter out empty strings from split result
        const lastActiveProjects = lastActiveProjectsRaw
          ?.split(',')
          .filter(Boolean)

        // Load impersonated active projects if they were stored
        const activeProjectsResponse =
          lastActiveProjects?.length &&
          (await client.query<GetActiveProjectsDetails>({
            query: GET_ACTIVE_PROJECTS_DETAILS,
            variables: { ids: lastActiveProjects },
          }))

        if (response.data && response.data.me) {
          // Merge REST API profile data with GraphQL data
          const userData = {
            ...response.data.me,
            // Override with REST API profile data (source of truth for language/timezone)
            language: profile.language,
            timezone: profile.timezone,
            datetimeFormatLocale: profile.datetime_format_locale,
            firstName: profile.first_name,
            lastName: profile.last_name,
            email: profile.email,
            logo: profile.logo,
            color: profile.color,
          }
          let finalActiveProjects = activeProjectsResponse
            ? activeProjectsResponse.data?.allProjects
            : userData.activeProjects
          let finalActiveProjectsIds = lastActiveProjects?.length
            ? lastActiveProjects
            : userData.activeProjectsIds

          // Fallback: if no active projects, auto-select all available projects
          if (!finalActiveProjectsIds?.length) {
            // Try ownProjectsIds first, then company's projectSet
            const allProjectIds =
              userData.ownProjectsIds?.length > 0
                ? userData.ownProjectsIds
                : userData.company?.projectSet?.edges
                    ?.map((edge: $TSFixMe) => edge?.node?.id)
                    .filter(Boolean)

            if (allProjectIds?.length) {
              const allProjectsResponse =
                await client.query<GetActiveProjectsDetails>({
                  query: GET_ACTIVE_PROJECTS_DETAILS,
                  variables: { ids: allProjectIds },
                })
              finalActiveProjects = allProjectsResponse.data?.allProjects
              finalActiveProjectsIds = allProjectIds
            }
          }

          setUser({
            ...userData,
            activeProjects: finalActiveProjects,
            activeProjectsIds: finalActiveProjectsIds,
          })
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('err', e)
      }
    }

    async function getSavedFilters() {
      try {
        const filtersResponse = await client.query({
          query: ALL_USER_FILTER_COLLECTIONS,
        })

        if (filtersResponse.data?.allUserFilterCollections) {
          setSavedFilters(
            filtersResponse.data.allUserFilterCollections.edges.map(
              ({ node }: $TSFixMe) => node
            )
          )
        }
      } catch (e) {
        // Ignore
      }
    }

    function setUserAsNonAuthenticated() {
      setRouting({
        roles: [`non-authenticated`],
        init: true,
      })
      TokenStorage.clearAll()
    }

    function checkTokenRefreshTokenOrLogout() {
      const iamToken = TokenStorage.getIAMAccessToken()

      if (!iamToken) {
        setUserAsNonAuthenticated()
        return
      }

      getUser()
      getSavedFilters()
    }

    checkTokenRefreshTokenOrLogout()
  }, [client, history])

  useEffect(() => {
    if (init && anyOtherActionRequired) {
      // check for any other action otherwise set it to false.

      if (!checkForRedirects(history)) {
        setAnyOtherActionRequired(false)
      }
    }
  }, [anyOtherActionRequired, history, init])

  if (!init || anyOtherActionRequired)
    return (
      <div className="fill-parent center-child">
        <Spin size="large" />
      </div>
    )

  return <>{children}</>
}

export default Router
